import { memo, useCallback, useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Popover, Transition } from '@headlessui/react';
import PlayerMenuTooltip from '../../../PlayerMenus/PlayerMenuTooltip';
import { ColorPicker } from '@/components/atoms/ColorPicker/ColorPicker';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { brandKit } from '@/stores/brandKit';
import { updateCaptionStyles } from '@/stores/clip';
import { classnames } from '@/libs/utils';
import Toggle from '@/components/atoms/Toggle';
import { FeatureFlagKeys } from '@/services/featureFlag';
import featureFlagStore from '@/stores/featureFlagStore';
import Icon from '@/components/atoms/Icon';

function CaptionOutlinePopover() {
  const { clipId, clipData } = useClipsContext();
  const brandKitStore = useSyncExternalStore(brandKit.subscribe, brandKit.getSnapshot);
  const featureFlags = useSyncExternalStore(featureFlagStore.subscribe, featureFlagStore.getSnapshot);
  const usePlatformBrandKit = !!featureFlags[FeatureFlagKeys.Use_CL_Platform_Brandkit];
  const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

  const [strokeWidth, setStrokeWidth] = useState<number>(10);
  const [strokeColor, setStrokeColor] = useState<string>('#000000');

  const enabled = useMemo(() => {
    return !!clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeWidth;
  }, [clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeWidth]);

  const currentStrokeWidth = useMemo(() => {
    const width = clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeWidth;
    if (typeof width === 'number') {
      return width;
    }
    if (typeof width === 'string') {
      return parseInt(width.replace('px', '')) || 0;
    }
    return 0;
  }, [clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeWidth]);

  const currentStrokeColor = useMemo(() => {
    return clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeColor || '#000000';
  }, [clipData.asset_metadata.caption_styles?.pageStyle?.WebkitTextStrokeColor]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (currentStrokeWidth > 0) {
      setStrokeWidth(currentStrokeWidth);
    }

    if (currentStrokeColor) {
      setStrokeColor(currentStrokeColor);
    }
  }, [enabled, currentStrokeWidth, currentStrokeColor]);

  const onToggleOutline = useCallback(() => {
    if (enabled) {
      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          WebkitTextStrokeWidth: undefined,
          WebkitTextStrokeColor: undefined,
          paintOrder: undefined
        }
      });
    } else {
      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          WebkitTextStrokeWidth: `${strokeWidth}px`,
          WebkitTextStrokeColor: strokeColor,
          paintOrder: 'stroke'
        }
      });
    }
  }, [enabled, clipId, clipData.asset_metadata.caption_styles?.pageStyle, strokeWidth, strokeColor]);

  const onStrokeWidthChange = useCallback(
    e => {
      const newWidth = Number(e.target.value);

      setStrokeWidth(newWidth);

      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          WebkitTextStrokeWidth: `${newWidth}px`,
          WebkitTextStrokeColor: strokeColor,
          paintOrder: 'stroke'
        }
      });
    },
    [clipId, clipData.asset_metadata.caption_styles?.pageStyle, strokeColor]
  );

  const onStrokeColorChange = useCallback(
    (value?: string) => {
      if (value) {
        setStrokeColor(value);
      }

      updateCaptionStyles(clipId, {
        pageStyle: {
          ...clipData.asset_metadata.caption_styles?.pageStyle,
          WebkitTextStrokeColor: value,
          WebkitTextStrokeWidth: `${strokeWidth}px`,
          paintOrder: 'stroke'
        }
      });
    },
    [clipId, clipData.asset_metadata.caption_styles?.pageStyle, strokeWidth]
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Popover.Button
            data-testid="captions-menu-outline-button"
            className={classnames(
              'group/button flex size-10 items-center justify-center rounded-md hover:bg-slate-100',
              open ? 'bg-slate-100' : 'hover:bg-slate-50'
            )}
          >
            <div className="flex w-10 cursor-pointer items-center justify-center rounded-md">
              <Icon name="IconScribble" size={16} />
              <PlayerMenuTooltip content="Outline" />
            </div>
          </Popover.Button>
          <Transition
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            afterLeave={() => setIsColorPickerOpen(false)}
          >
            <Popover.Panel className="absolute z-50 mt-1 w-64 translate-y-1 space-y-5">
              <div className="rounded-lg border bg-white shadow-lg">
                {usePlatformBrandKit && isColorPickerOpen ? (
                  <ColorPicker
                    defaultColorsLabel="Outline Color"
                    onChange={onStrokeColorChange}
                    hex={strokeColor}
                    inline
                    onBack={() => setIsColorPickerOpen(false)}
                  />
                ) : (
                  <div className="space-y-4 p-4">
                    {/* Enable/Disable Outline */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">Outline</span>
                      <Toggle on={enabled} onToggle={onToggleOutline} />
                    </div>

                    {enabled && (
                      <>
                        {/* Stroke Width */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-3 text-sm font-medium">Width</span>
                          <span className="col-span-9 flex items-center gap-2">
                            <input
                              type="range"
                              min={0}
                              max={20}
                              value={strokeWidth}
                              step={0.5}
                              onChange={onStrokeWidthChange}
                              className="h-1 w-full cursor-pointer appearance-none rounded-xl bg-slate-300"
                            />
                            <span className="ml-1 w-6 shrink-0 text-right text-2xs tabular-nums text-slate-400">
                              {strokeWidth}
                            </span>
                          </span>
                        </div>

                        {/* Stroke Color */}
                        <div className="grid grid-cols-12 items-center justify-between gap-2">
                          <span className="col-span-3 text-sm font-medium">Color</span>
                          <ColorPicker
                            defaultColors={brandKitStore.colors}
                            defaultColorsLabel="Outline Color"
                            onChange={onStrokeColorChange}
                            hex={strokeColor}
                            onOpen={usePlatformBrandKit ? () => setIsColorPickerOpen(true) : undefined}
                            children={
                              <div className="flex size-full items-center justify-between bg-white p-2">
                                <div className="size-6 rounded border" style={{ background: strokeColor }} />
                                <span className="font-mono text-xs text-slate-400">{strokeColor}</span>
                              </div>
                            }
                            classNames="relative col-span-9 flex w-full items-center justify-between rounded-md border overflow-hidden"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

export default memo(CaptionOutlinePopover);
